import {IVanillaReaderUI} from './IVanillaReaderUI';
import {IVanillaReaderUiOptionsData} from './VanillaReaderUiModel';
import {IVanillaReaderUiColorTheme} from '../VanillaReader/VanillaReaderModel';
import {IVanillaReaderUiComponentController} from './IVanillaReaderUiComponentController';
import {VanillaReaderUI} from './VanillaReaderUI';
import {VanillaReaderAppAssets} from "../VanillaReaderApp/VanillaReaderAppAssets";

export interface VanillaReaderUiDialogSettingsController extends IVanillaReaderUiComponentController, IVanillaReaderUI {
}

/**
 * # VanillaReaderUiDialogSettingsController
 *
 * ## RESPONSIBILITIES
 * This controller mixing class handles all event callbacks from the `VanillaReaderUiDialogSettings` class.
 *
 * Just as all other controller mixin classes, this class is used to add functionality to the `VanillaReaderUi` class
 * in a "modular" way. Read more about this in the `UI Components and Controller Mixins` in the README.md file.
 *
 * ## RELATED TYPES
 * - VanillaReaderUi
 * - VanillaReaderUiDialogSettings
 *
 */
export class VanillaReaderUiDialogSettingsController {

    static __initialize__(vanillaReaderUiContext: VanillaReaderUI): void {
        vanillaReaderUiContext.dialogSettings.onSettingsUpdated(vanillaReaderUiContext._onDialogSettingsUpdated.bind(vanillaReaderUiContext));
        vanillaReaderUiContext.dialogSettings.onSettingsReset(vanillaReaderUiContext._onDialogSettingsReset.bind(vanillaReaderUiContext));
    }

    _onDialogSettingsReset(): void {
        this._resetAllOptions();
        this.toaster.toast('View settings have been reset to defaults.', true).catch(console.warn);
    }

    _onDialogSettingsUpdated(settings: IVanillaReaderUiOptionsData): void {

        if (settings.useAccessibilityMode !== this.isInAccessibilityMode) {
            if (settings.useAccessibilityMode) {
                this.enterAccessibilityMode();
                this.dialogSettings.close();
            } else {
                this.exitAccessibilityMode();
                this.dialogSettings.close();
            }
            return;
        }

        // The Reading Ruler options are a bit special as they do not affect the VanillaReader module at all.
        // Thus, we can apply the changes here in this module.
        if (settings.readingRulerOptions) {
            let paletteAsset: IVanillaReaderUiColorTheme |undefined = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === settings.activeUiPaletteName);
            let defaultPaletteAsset = VanillaReaderAppAssets.ColorThemes.find(palette => palette.name === 'Dark')!;
            this.readingRuler.setOptions({
                lineBoxBackgroundColor: settings.readingRulerOptions.lineBoxBackgroundColor,
                overlayIsVisible: settings.readingRulerOptions.overlayIsVisible,
                lineBoxLineSpan: settings.readingRulerOptions.lineBoxLineSpan,
                snapToLineBoxes: settings.readingRulerOptions.snapToLineBoxes,
                overlayBackgroundColor: paletteAsset?.backgroundDark || defaultPaletteAsset.backgroundDark
            });

            // Make sure that the overlay color matches the palette
            settings.readingRulerOptions.overlayBackgroundColor = paletteAsset?.backgroundDark || defaultPaletteAsset.backgroundDark;
        }

        // Check if the media player is open and if so save it along with the other settings data
        settings.showNarrationControls = this.menuMediaPlayer.isOpen();

        this.vanillaReaderAPI.setAppUiOptions(settings);
    };

    private _resetAllOptions(): void {
        this.vanillaReaderAPI.resetAppUiOptions();
    }
}
